import React, { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import toast from "react-hot-toast";
import { Trash2 } from "lucide-react";
import SingleSelect, {
  SingleSelectOption
} from "@/components/controls/SingleSelect";
import { Button } from "@/components/ui/button";
import { IconButton } from "@/components/ui/icon-button";
import Input from "@/components/ui/input";
import Text from "@/components/ui/text";
import { useProducts } from "@/entities/settings/hooks";
import { useSaveStripeMapping } from "@/entities/stripeCheckout/hooks";
import { ContractMapping } from "@/entities/stripeCheckout/types";
import { webFormErrorDetail } from "@/entities/webForm/hooks";
import { FieldValuesEditor } from "@/pages/settings/components/integrations/stripeCheckout/FieldValuesEditor";
import {
  ContractDetailsDraft,
  FIELD_MAP_SUGGESTIONS,
  FieldMapRow,
  MAX_BUFFER_DAYS,
  MAX_CUSTOM_FIELDS,
  MAX_NAME_LENGTH,
  NAME_PLACEHOLDERS,
  STRIPE_CHECKOUT_PREFIX,
  contractDetailsDraft,
  contractDetailsErrors,
  mappingWithDetails
} from "@/pages/settings/components/integrations/stripeCheckout/stripeCheckoutOptions";
import Section from "@/pages/settings/components/sections/Section";

const PREFIX = `${STRIPE_CHECKOUT_PREFIX}.contract`;
const SUGGESTIONS_ID = "stripe-checkout-field-map-suggestions";

const SELECT_CLASSNAME = "text-start border border-border-border2 pl-0";
const SELECT_TRIGGER_CLASSNAME = "w-full min-h-8 h-9";

interface ContractDetailsSectionProps {
  mapping: ContractMapping | undefined;
  isLoading: boolean;
  subTitle: string;
  isBusy: boolean;
}

export const ContractDetailsSection: React.FC<ContractDetailsSectionProps> = ({
  mapping,
  isLoading,
  subTitle,
  isBusy
}) => {
  const { t } = useTranslation();
  const save = useSaveStripeMapping();
  const productsQuery = useProducts();
  const nameInput = useRef<HTMLInputElement>(null);
  const [saveError, setSaveError] = useState<string | null>(null);

  const [draft, setDraft] = useState<ContractDetailsDraft | null>(
    mapping ? contractDetailsDraft(mapping) : null
  );
  // Keyed on this section's fields alone, so saving the prices keeps a draft.
  const saved = mapping ? JSON.stringify(contractDetailsDraft(mapping)) : null;
  const [synced, setSynced] = useState(saved);
  if (saved !== synced) {
    setSynced(saved);
    if (mapping) setDraft(contractDetailsDraft(mapping));
  }

  const products = productsQuery.data?.products ?? [];
  const productOptions: SingleSelectOption[] = products.map((product) => ({
    id: product.id,
    displayName: product.name
  }));
  // A saved product line the catalog no longer has still shows, marked.
  if (
    draft?.productId &&
    productsQuery.isSuccess &&
    !productOptions.some((option) => option.id === draft.productId)
  ) {
    productOptions.push({
      id: draft.productId,
      displayName: t(`${PREFIX}.productNotInCatalog`)
    });
  }

  const errors = draft ? contractDetailsErrors(draft) : [];
  const disabled = !mapping || isBusy || save.isPending;
  const isDirty =
    saved !== null && draft !== null && JSON.stringify(draft) !== saved;
  const canSave = isDirty && errors.length === 0 && !disabled;

  const edit = (changes: Partial<ContractDetailsDraft>) => {
    setDraft((current) => (current ? { ...current, ...changes } : current));
    setSaveError(null);
  };

  const updateFieldMap = (index: number, changes: Partial<FieldMapRow>) =>
    draft &&
    edit({
      fieldMap: draft.fieldMap.map((row, i) =>
        i === index ? { ...row, ...changes } : row
      )
    });

  // Puts the placeholder where the cursor is, or at the end.
  const insertPlaceholder = (name: string) => {
    if (!draft) return;
    const text = `{${name}}`;
    const input = nameInput.current;
    const start = input?.selectionStart ?? draft.nameTemplate.length;
    const end = input?.selectionEnd ?? start;
    edit({
      nameTemplate:
        draft.nameTemplate.slice(0, start) +
        text +
        draft.nameTemplate.slice(end)
    });
  };

  const handleSave = () => {
    if (!mapping || !draft) return;
    setSaveError(null);
    save.mutate(
      { data: { mapping: mappingWithDetails(mapping, draft) } },
      {
        onSuccess: () => toast.success(t(`${PREFIX}.saved`)),
        onError: (error) => {
          const message = webFormErrorDetail(error) ?? t(`${PREFIX}.saveError`);
          setSaveError(message);
          toast.error(message);
        }
      }
    );
  };

  return (
    <Section
      title={t(`${PREFIX}.title`)}
      subTitle={subTitle}
      isLoading={isLoading}
      items={[
        {
          title: t(`${PREFIX}.nameTemplate.title`),
          subTitle: t(`${PREFIX}.nameTemplate.subtitle`),
          customAction: (
            <div className="flex w-[320px] flex-col gap-2">
              <Input
                ref={nameInput}
                data-test="stripe-checkout-name-template"
                aria-label={t(`${PREFIX}.nameTemplate.title`)}
                value={draft?.nameTemplate ?? ""}
                onChange={(event) => edit({ nameTemplate: event.target.value })}
                maxLength={MAX_NAME_LENGTH}
                disabled={disabled}
              />
              <div className="flex flex-wrap gap-1">
                {NAME_PLACEHOLDERS.map((name) => (
                  <Button
                    key={name}
                    variant="secondary"
                    className="h-6 px-1.5 font-mono text-xs"
                    data-test={`stripe-checkout-placeholder-${name}`}
                    aria-label={t(`${PREFIX}.nameTemplate.insert`, { name })}
                    onClick={() => insertPlaceholder(name)}
                    disabled={disabled}
                  >
                    {`{${name}}`}
                  </Button>
                ))}
              </div>
            </div>
          )
        },
        {
          title: t(`${PREFIX}.product.title`),
          subTitle: t(`${PREFIX}.product.subtitle`),
          customAction: (
            <div className="w-[240px]">
              <SingleSelect
                fieldName="stripeCheckoutProduct"
                options={productOptions}
                selectedValue={draft?.productId ?? undefined}
                onSelection={(value) => edit({ productId: String(value) })}
                formatter={(value) =>
                  productOptions.find((option) => option.id === value)
                    ?.displayName ?? String(value)
                }
                placeholder={t(`${PREFIX}.product.placeholder`)}
                clearable
                onClear={() => edit({ productId: null })}
                showSearch
                disabled={disabled}
                isLoading={productsQuery.isLoading}
                className={SELECT_CLASSNAME}
                popoverTriggerClassName={SELECT_TRIGGER_CLASSNAME}
                variant="form"
              />
            </div>
          )
        },
        {
          title: t(`${PREFIX}.bufferDays.title`),
          subTitle: t(`${PREFIX}.bufferDays.subtitle`),
          customAction: (
            <Input
              data-test="stripe-checkout-buffer-days"
              aria-label={t(`${PREFIX}.bufferDays.title`)}
              type="number"
              min={0}
              max={MAX_BUFFER_DAYS}
              value={draft?.bufferDays ?? ""}
              onChange={(event) => edit({ bufferDays: event.target.value })}
              disabled={disabled}
              containerClassName="w-[120px]"
            />
          )
        }
      ]}
    >
      <div className="flex flex-col gap-2">
        <Text variant="lgLabel">{t(`${PREFIX}.customFields.title`)}</Text>
        <Text variant="bodySecondary">
          {t(`${PREFIX}.customFields.subtitle`)}
        </Text>
        <FieldValuesEditor
          rows={draft?.customFields ?? []}
          onChange={(customFields) => edit({ customFields })}
          disabled={disabled}
          testId="stripe-checkout-custom-fields"
        />
      </div>
      <div className="flex flex-col gap-2">
        <Text variant="lgLabel">{t(`${PREFIX}.fieldMap.title`)}</Text>
        <Text variant="bodySecondary">{t(`${PREFIX}.fieldMap.subtitle`)}</Text>
        <datalist id={SUGGESTIONS_ID}>
          {FIELD_MAP_SUGGESTIONS.map((field) => (
            <option key={field} value={field} />
          ))}
        </datalist>
        {(draft?.fieldMap ?? []).map((row, index) => (
          <div
            key={index}
            className="flex items-center gap-2"
            data-test={`stripe-checkout-field-map-${index}`}
          >
            <Input
              data-test={`stripe-checkout-field-map-source-${index}`}
              aria-label={t(`${PREFIX}.fieldMap.a11y.source`, {
                position: index + 1
              })}
              placeholder={t(`${PREFIX}.fieldMap.source`)}
              list={SUGGESTIONS_ID}
              value={row.source}
              onChange={(event) =>
                updateFieldMap(index, { source: event.target.value })
              }
              maxLength={MAX_NAME_LENGTH}
              disabled={disabled}
              containerClassName="flex-1"
            />
            <Input
              data-test={`stripe-checkout-field-map-target-${index}`}
              aria-label={t(`${PREFIX}.fieldMap.a11y.target`, {
                position: index + 1
              })}
              placeholder={t(`${PREFIX}.fieldMap.target`)}
              value={row.target}
              onChange={(event) =>
                updateFieldMap(index, { target: event.target.value })
              }
              maxLength={MAX_NAME_LENGTH}
              disabled={disabled}
              containerClassName="flex-1"
            />
            <IconButton
              variant="iconNoBg"
              size="lg"
              data-test={`stripe-checkout-field-map-remove-${index}`}
              aria-label={t(`${PREFIX}.fieldMap.a11y.remove`, {
                position: index + 1
              })}
              onClick={() =>
                draft &&
                edit({ fieldMap: draft.fieldMap.filter((_, i) => i !== index) })
              }
              disabled={disabled}
            >
              <Trash2 />
            </IconButton>
          </div>
        ))}
        <Button
          variant="tertiary"
          className="w-fit"
          data-test="stripe-checkout-field-map-add"
          onClick={() =>
            draft &&
            edit({ fieldMap: [...draft.fieldMap, { source: "", target: "" }] })
          }
          disabled={
            disabled || (draft?.fieldMap.length ?? 0) >= MAX_CUSTOM_FIELDS
          }
        >
          {t(`${PREFIX}.fieldMap.add`)}
        </Button>
      </div>
      {(errors.length > 0 || saveError) && (
        <div
          className="flex flex-col gap-1"
          role="alert"
          data-test="stripe-checkout-contract-error"
        >
          {errors.map((error) => (
            <Text key={error} variant="smBodyError">
              {t(`${PREFIX}.errors.${error}`)}
            </Text>
          ))}
          {saveError && <Text variant="smBodyError">{saveError}</Text>}
        </div>
      )}
      <div className="flex justify-end">
        <Button
          onClick={handleSave}
          disabled={!canSave}
          data-test="stripe-checkout-save-contract"
        >
          {t(`${PREFIX}.save`)}
        </Button>
      </div>
    </Section>
  );
};
